// components/tier-branch/BranchTable.tsx
// Branch Listing table: id, name, new users today, lock states, credit
// limit, WhatsApp link, status, and per-row actions.
import React from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip,
  Chip, Link, Box,
} from "@mui/material";
import {
  Lock, LockOpen, Edit, People, CheckCircle, Block, AccountBalance,
} from "@mui/icons-material";
import type { Branch } from "../../types/tierBranch";
import { fmt } from "./useSnack";

const LockCell: React.FC<{ locked: boolean }> = ({ locked }) => (
  <TableCell align="center">
    {locked ? (
      <Lock fontSize="small" color="error" />
    ) : (
      <LockOpen fontSize="small" color="success" />
    )}
  </TableCell>
);

const BranchTable: React.FC<{
  branches: Branch[];
  canManage: boolean;
  onEdit: (b: Branch) => void;
  onViewUsers: (b: Branch) => void;
  onToggleStatus: (b: Branch) => void;
  onSettlement?: (b: Branch) => void;
}> = ({ branches, canManage, onEdit, onViewUsers, onToggleStatus, onSettlement }) => {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Id</TableCell>
          <TableCell>Branch Name</TableCell>
          <TableCell align="center">New Users Today</TableCell>
          <TableCell align="center">Reg.</TableCell>
          <TableCell align="center">Bet</TableCell>
          <TableCell align="center">Deposit</TableCell>
          <TableCell align="center">Withdraw</TableCell>
          <TableCell align="center">Batch W/D</TableCell>
          <TableCell align="right">Credit Limit</TableCell>
          <TableCell>WhatsApp</TableCell>
          <TableCell align="center">Status</TableCell>
          <TableCell align="center">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {branches.map((b) => (
          <TableRow key={b.id} hover>
            <TableCell>{b.id}</TableCell>
            <TableCell>
              {onSettlement ? (
                <Link component="button" type="button" underline="hover" onClick={() => onSettlement(b)} sx={{ textAlign: "left" }}>
                  {b.name}
                </Link>
              ) : b.name}
            </TableCell>
            <TableCell align="center">{b.newUsersToday ?? 0}</TableCell>
            <LockCell locked={b.registrationLocked} />
            <LockCell locked={b.betLocked} />
            <LockCell locked={b.depositLocked} />
            <LockCell locked={b.withdrawLocked} />
            <LockCell locked={b.batchedWithdrawal} />
            <TableCell align="right">{fmt(b.creditLimit)}</TableCell>
            <TableCell>
              {b.whatsappNo ? (
                <Link
                  href={b.whatsappLink || b.whatsappNo}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                >
                  {b.whatsappNo}
                </Link>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell align="center">
              <Chip
                size="small"
                color={b.enabled ? "success" : "default"}
                icon={b.enabled ? <CheckCircle /> : <Block />}
                label={b.enabled ? "Active" : "Off"}
                onClick={canManage ? () => onToggleStatus(b) : undefined}
                clickable={canManage}
              />
            </TableCell>
            <TableCell align="center">
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Tooltip title="Users">
                  <IconButton size="small" onClick={() => onViewUsers(b)}>
                    <People fontSize="small" />
                  </IconButton>
                </Tooltip>
                {onSettlement && (
                  <Tooltip title="Settlement & P&L">
                    <IconButton size="small" onClick={() => onSettlement(b)}>
                      <AccountBalance fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canManage && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => onEdit(b)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default BranchTable;
